import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lightning } from "@phosphor-icons/react";

export default function AuthCallback() {
  const nav = useNavigate();
  const { setUserDirect } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // GitHub OAuth (Phase 12): backend has set httpOnly cookie + redirected with
    // ?provider=github. Just call /me with the cookie.
    const params = new URLSearchParams(window.location.search);
    if (params.get("provider") === "github") {
      (async () => {
        try {
          const { data: user } = await api.get("/auth/me");
          localStorage.setItem("cf_user", JSON.stringify(user));
          setUserDirect(user);
          nav("/app", { replace: true });
        } catch {
          nav("/auth?error=github_session");
        }
      })();
      return;
    }

    const hash = window.location.hash;

    // Legacy GitHub callback (pre-Phase-12) — Phase 12 backend redirects to
    // ?provider=github (no hash). If someone still has an old session in
    // flight, treat the hash token as one-shot: post it via cookie-set path.
    const ghMatch = hash.match(/token=([^&]+).*provider=github/);
    if (ghMatch) {
      (async () => {
        try {
          // Phase 12: backend already set the cookie via /github/callback. We
          // never persist the JWT client-side — just fetch the user.
          const { data: user } = await api.get("/auth/me");
          localStorage.setItem("cf_user", JSON.stringify(user));
          setUserDirect(user);
          nav("/app", { replace: true });
        } catch {
          nav("/auth?error=github_session");
        }
      })();
      return;
    }

    // Emergent Google OAuth callback: #session_id=xxx
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/auth"); return; }
    const session_id = m[1];

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        // Backend sets httpOnly session_token cookie. We only cache the user.
        if (data.user) localStorage.setItem("cf_user", JSON.stringify(data.user));
        setUserDirect(data.user);
        nav("/app", { replace: true });
      } catch (e) {
        nav("/auth?error=oauth");
      }
    })();
  }, [nav, setUserDirect]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 rounded-md bg-[#FF3B6B] flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Lightning weight="fill" size={24} />
        </div>
        <p className="text-white/60">Signing you in...</p>
      </div>
    </div>
  );
}
